import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

import type { IDialerUser, IDialerTenant, RemainingLead, IDialerCampaign } from '../dialer-campaign.types';

export const dialerCampaignApi = createApi({
  reducerPath: 'dialerCampaignApi',
  baseQuery,
  tagTypes: ['DialerCampaign', 'DialerTenant', 'DialerUser'],
  endpoints: (builder) => ({
    // ==========================================
    // A. Tenants Endpoints
    // ==========================================
    getDialerTenantList: builder.query<IDialerTenant[], void>({
      query: () => ({
        url: 'api/dialer/tenants',
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        const resData = response?.success ? response.data : response;
        if (Array.isArray(resData)) return resData;
        if (resData && Array.isArray(resData.data)) return resData.data;
        if (resData && resData.list && Array.isArray(resData.list)) return resData.list;
        return [];
      },
      providesTags: ['DialerTenant'],
    }),

    addDialerTenant: builder.mutation<any, IDialerTenant>({
      query: (body) => ({
        url: 'api/dialer/tenants',
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['DialerTenant'],
    }),

    updateDialerTenant: builder.mutation<any, { id: string | number; updates: Partial<IDialerTenant> }>({
      query: ({ id, updates }) => ({
        url: `api/dialer/tenants/${id}`,
        method: 'PATCH',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { id }) => ['DialerTenant', { type: 'DialerTenant', id }],
    }),

    deleteDialerTenant: builder.mutation<any, string | number>({
      query: (id) => ({
        url: `api/dialer/tenants/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['DialerTenant'],
    }),

    // ==========================================
    // B. Users Endpoints
    // ==========================================
    getDialerUserList: builder.query<IDialerUser[], void>({
      query: () => ({
        url: 'api/dialer/users',
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        const resData = response?.success ? response.data : response;
        if (Array.isArray(resData)) return resData;
        if (resData && Array.isArray(resData.data)) return resData.data;
        if (resData && resData.list && Array.isArray(resData.list)) return resData.list;
        return [];
      },
      providesTags: ['DialerUser'],
    }),

    addDialerUser: builder.mutation<any, IDialerUser>({
      query: (body) => ({
        url: 'api/dialer/users',
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['DialerUser'],
    }),

    updateDialerUser: builder.mutation<any, { id: string | number; updates: Partial<IDialerUser> }>({
      query: ({ id, updates }) => ({
        url: `api/dialer/users/${id}`,
        method: 'PATCH',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { id }) => ['DialerUser', { type: 'DialerUser', id }],
    }),

    deleteDialerUser: builder.mutation<any, string | number>({
      query: (id) => ({
        url: `api/dialer/users/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['DialerUser'],
    }),

    // ==========================================
    // C. Campaigns Endpoints
    // ==========================================
    getDialerCampaignList: builder.query<IDialerCampaign[], void>({
      query: () => ({
        url: 'api/dialer/campaigns',
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        const resData = response?.success ? response.data : response;
        if (Array.isArray(resData)) {
          return resData;
        }
        if (resData && Array.isArray(resData.data)) {
          return resData.data;
        }
        if (resData && resData.list && Array.isArray(resData.list)) {
          return resData.list;
        }
        return [];
      },
      providesTags: ['DialerCampaign'],
    }),

    getDialerCampaignDetails: builder.query<IDialerCampaign | null, string | number>({
      query: () => ({
        url: 'api/dialer/campaigns',
        method: 'GET',
      }),
      transformResponse: (response: any, meta, arg) => {
        const resData = response?.success ? response.data : response;
        let list: IDialerCampaign[] = [];
        if (Array.isArray(resData)) {
          list = resData;
        } else if (resData && Array.isArray(resData.data)) {
          list = resData.data;
        } else if (resData && resData.list && Array.isArray(resData.list)) {
          list = resData.list;
        }
        return list.find((item) => String(item.id) === String(arg)) || null;
      },
      providesTags: (result, error, arg) => [{ type: 'DialerCampaign', id: arg }],
    }),

    addDialerCampaign: builder.mutation<any, Partial<IDialerCampaign>>({
      query: (body) => ({
        url: 'api/dialer/campaigns',
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['DialerCampaign'],
    }),

    updateDialerCampaign: builder.mutation<any, { id: string | number; updates: Partial<IDialerCampaign> }>({
      query: ({ id, updates }) => ({
        url: `api/dialer/campaigns/${id}`,
        method: 'PATCH',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { id }) => ['DialerCampaign', { type: 'DialerCampaign', id }],
    }),

    deleteDialerCampaign: builder.mutation<any, string | number>({
      query: (id) => ({
        url: `api/dialer/campaigns/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['DialerCampaign'],
    }),

    addDialerNumbers: builder.mutation<any, { id: string | number; body: { data?: [string, string][]; contactIds?: string[] } }>({
      query: ({ id, body }) => ({
        url: `api/dialer/campaigns/${id}/numbers`,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: (result, error, { id }) => ['DialerCampaign', { type: 'DialerCampaign', id }],
    }),

    getDialerRemaining: builder.query<RemainingLead[], string | number>({
      query: (id) => ({
        url: `api/dialer/campaigns/${id}/remaining`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        const resData = response?.success ? response.data : response;
        if (Array.isArray(resData)) {
          return resData;
        }
        if (resData && Array.isArray(resData.data)) {
          return resData.data;
        }
        if (resData && resData.list && Array.isArray(resData.list)) {
          return resData.list;
        }
        return [];
      },
      providesTags: (result, error, id) => [{ type: 'DialerCampaign', id }],
    }),
  }),
});

export const {
  useGetDialerTenantListQuery,
  useAddDialerTenantMutation,
  useUpdateDialerTenantMutation,
  useDeleteDialerTenantMutation,
  useGetDialerUserListQuery,
  useAddDialerUserMutation,
  useUpdateDialerUserMutation,
  useDeleteDialerUserMutation,
  useGetDialerCampaignListQuery,
  useGetDialerCampaignDetailsQuery,
  useAddDialerCampaignMutation,
  useUpdateDialerCampaignMutation,
  useDeleteDialerCampaignMutation,
  useAddDialerNumbersMutation,
  useGetDialerRemainingQuery,
} = dialerCampaignApi;
